import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { deleteProcess, loadStages, updateProcessState } from '../../actions/processActions';
import { loadMatrix } from '../../actions/peiActions';

import { Card, Text, Input, Button, Table, Badge, Stack, Flex, Select, Menu, ActionIcon, Modal, Group, Loader, Center, } from '@mantine/core';
import { IconSearch, IconPlus, IconDotsVertical, IconTrash, IconSwitch, IconEdit } from '@tabler/icons-react';

import ModalProcessEditor from './ModalProcessEditor';
import { getProcessActualStage, getProcessDatePeriod } from '../../utils/processHelpers'
import { formatDate } from '../../utils/dateUtils';
import APP_STATES from '../../enums/AppStates';
import ModalDialog from '../ModalDialog';

const estadoColor = {
  [APP_STATES.ACTIVO]: 'green',
  [APP_STATES.CERRADO]: 'gray',
  [APP_STATES.BORRADOR]: 'gray',
  [APP_STATES.HISTORICO]: 'blue',
};
const DashboardProcess = ({ processes, loading }) => {

  const dispatch = useDispatch();
  const {user} = useSelector((state) => state.auth);
  const {data: stages, loading: loadingStages} = useSelector((state) => state.process.stages)
  const {data: matrix, loading: loadingMatrix} = useSelector((state) => state.pei.matrix)
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');

  const [editorOpened, setEditorOpened] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  
  const [changeStateModalOpened, setChangeStateModalOpened] = useState(false);
  const [processToChangeState, setProcessToChangeState] = useState(null);

  const [newEstado, setNewEstado] = useState('');

   
  useEffect(()=>{
    dispatch(loadStages(null,null,null,user));
    dispatch(loadMatrix(user));
  },[])

  const handleEditProcess = (process) => {
    setSelectedProcess(process);
    setEditorOpened(true);
  }

  const handleDeleteClick = (process) => {
    setProcessToDelete(process);
    setDeleteModalOpened(true);
  };

  const confirmDelete = () => {
    dispatch(deleteProcess(processToDelete.Id, user));
    setDeleteModalOpened(false);
  };

  const handleChangeStateClick = (process) => {
    setProcessToChangeState(process);
    setNewEstado(process?.estado?.Id || '');
    setChangeStateModalOpened(true);
  };

  const confirmChangeState = () => {
    dispatch(updateProcessState(processToChangeState.Id,newEstado, user));
    setChangeStateModalOpened(false);
  };

  
  const filteredProcesos = processes?.filter((p) => {
    const estadoId = p?.estado?.Id?.toUpperCase();
    const matchEstado = !estado || estadoId === estado;
    const matchTexto = p.nombre.toLowerCase().includes(search.toLowerCase());
    return matchEstado && matchTexto;
  });
  
  return (
    <Card shadow='sm' p="md" radius={'md'}>
      <Stack spacing="lg">
        <Flex justify="space-between" align="center" wrap="wrap">
          <div>
            <Text size='xl' fw={600}>Procesos Evaluativos</Text>
            <Text size="sm" c="dimmed">Gestiona los procesos de evaluación institucional</Text>
          </div>
          <Button 
            leftSection={<IconPlus size={16} />} 
            color="#e72166"
            onClick={() => handleEditProcess(null)}
          >
            Nuevo Proceso
          </Button>
        </Flex>

        <Flex direction={{ base: 'column', sm: 'row' }} gap="md" align="center">
          <Input
            leftSection={<IconSearch size={16} />}
            placeholder="Buscar procesos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            flex={3}
          />
          <Select
            flex={1}
            placeholder='Filtrar por estado'
            checkIconPosition='right'
            value={estado}
            onChange={setEstado}
            data={[
              { label: 'Activos', value: APP_STATES.ACTIVO },
              { label: 'Borradores', value: APP_STATES.BORRADOR },
              { label: 'Historicos', value: APP_STATES.HISTORICO },
            ]}
          />
        </Flex>
        <Card withBorder p={'md'} shadow='none'>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nombre</Table.Th>
                <Table.Th>Periodo</Table.Th>
                <Table.Th>Estado</Table.Th>
                <Table.Th>Etapa Actual</Table.Th>
                <Table.Th>Participantes</Table.Th>
                <Table.Th>Acciones</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center>
                      <Loader type='dots' color='gray'/>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : 
                filteredProcesos?.length > 0 ? (
                  filteredProcesos?.map((p) => {
                    const { startDate, endDate} = getProcessDatePeriod(p);
                    const actualStage = getProcessActualStage(p);
                    
                    return (
                      <Table.Tr key={p.Id}>
                        <Table.Td>
                          <Text span inherit fw={500}>{p.nombre}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text span inherit fw={400}>{formatDate(startDate)}</Text> - <Text span inherit fw={400}>{formatDate(endDate)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={estadoColor[p.estado.Id.toUpperCase()] || 'gray'} variant="light">
                            {p.estado.Codigo}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{actualStage ? actualStage.nombre : '-'}</Table.Td>
                        <Table.Td>{p.participantes?.length || '-'}</Table.Td>
                        <Table.Td>
                          <Menu shadow="md" width={200} withArrow>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray.9" aria-label="Settings">
                                <IconDotsVertical size={20}/>
                              </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                              <Menu.Label>Acciones</Menu.Label>
                              <Menu.Item leftSection={<IconSwitch size={14} />} onClick={() => handleChangeStateClick(p)}>
                                Cambiar estado
                              </Menu.Item>
                              <Menu.Item 
                                disabled={p.estado.Id.toUpperCase() === APP_STATES.HISTORICO}
                                leftSection={<IconEdit size={14} />}
                                onClick={() => handleEditProcess(p)}
                              >
                                Editar proceso
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                color="red"
                                disabled={p.estado.Id.toUpperCase() !== APP_STATES.BORRADOR}
                                leftSection={<IconTrash size={14} />}
                                onClick={() => handleDeleteClick(p)}
                              >
                                Eliminar
                              </Menu.Item>
                              
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center">
                        No se encontraron procesos.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )
              }
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>
      <ModalProcessEditor
        opened={editorOpened}
        onClose={() => setEditorOpened(false)}
        process={selectedProcess}
        stages={stages}
        matrix={matrix}
      />
      <ModalDialog
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={confirmDelete}
        title="Confirmar eliminación"
        description={`¿Estás seguro que querés eliminar el proceso "${processToDelete?.nombre}"? Esta acción no puede deshacerse.`}
        confirmColor="red"
      />
      <Modal
        opened={changeStateModalOpened}
        onClose={() => setChangeStateModalOpened(false)}
        title={(<b>Cambiar estado del proceso</b>)}
        centered
      >
        <Stack>
          <Select
            label="Seleccionar nuevo estado"
            value={newEstado}
            onChange={setNewEstado}
            data={[
              { label: 'Borrador', value: APP_STATES.BORRADOR },
              { label: 'Activo', value: APP_STATES.ACTIVO },
              { label: 'Histórico', value: APP_STATES.HISTORICO },
            ]}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setChangeStateModalOpened(false)}>Cancelar</Button>
            <Button onClick={confirmChangeState} color="blue">Guardar</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
};

export default DashboardProcess;
